/**
 * InfoModal - Modal informativo para formato de Excel
 */
import React from 'react';
import { Info, X, AlertTriangle, CheckCircle } from 'lucide-react';

interface InfoModalProps {
  type: 'informacoes' | 'itens';
  onClose: () => void;
}

export const InfoModal: React.FC<InfoModalProps> = ({ type, onClose }) => {
  const isInformacoes = type === 'informacoes';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`bg-gradient-to-r ${isInformacoes ? 'from-emerald-500 to-teal-500' : 'from-blue-500 to-indigo-500'} px-6 py-4 rounded-t-xl flex items-center justify-between sticky top-0`}>
          <div className="flex items-center space-x-3">
            <Info className="text-white" size={24} />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - {isInformacoes ? 'Informações da Contagem' : 'Itens da Contagem'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className={`bg-gradient-to-r ${isInformacoes ? 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-700' : 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700'} rounded-lg p-4 border-2`}>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {isInformacoes ? (
                <>
                  {/* Aviso importante */}
                  <div className="bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500 p-3 rounded mb-4">
                    <p className="text-amber-800 dark:text-amber-200 text-sm font-medium flex items-center gap-2">
                      <AlertTriangle size={18} />
                      <strong>IMPORTANTE:</strong> Preencha seus dados na <strong>LINHA 2</strong> do Excel!
                    </p>
                  </div>

                  {/* Estrutura do Excel */}
                  <div className="mb-4">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 mb-2">📋 Estrutura do Excel</p>
                    <div className="bg-white dark:bg-gray-700 rounded p-3 text-xs border border-gray-200 dark:border-gray-600">
                      <table className="w-full">
                        <tbody>
                          <tr>
                            <td className="text-gray-500 pr-3">Linha 1:</td>
                            <td className="text-blue-600 dark:text-blue-400 font-medium">Cabeçalhos (não editar)</td>
                          </tr>
                          <tr>
                            <td className="text-gray-500 pr-3">Linha 2:</td>
                            <td className="text-red-600 dark:text-red-400 font-bold">→ SEUS DADOS AQUI</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Colunas detalhadas */}
                  <div className="mb-4">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 mb-2">📊 Colunas (7 colunas, todas opcionais)</p>
                    <div className="bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-100 dark:bg-gray-600">
                            <th className="px-2 py-1.5 text-left">Col</th>
                            <th className="px-2 py-1.5 text-left">Campo</th>
                            <th className="px-2 py-1.5 text-left">Formato</th>
                            <th className="px-2 py-1.5 text-left">Exemplo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-mono px-1.5 py-0.5 rounded">A</span></td>
                            <td className="px-2 py-1.5 font-medium">Ordem Coleta</td>
                            <td className="px-2 py-1.5 text-gray-500">Texto livre</td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">OC-534-001</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-mono px-1.5 py-0.5 rounded">B</span></td>
                            <td className="px-2 py-1.5 font-medium">Lacre(s)</td>
                            <td className="px-2 py-1.5 text-gray-500">Texto livre</td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">LAC-123, LAC-456</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-mono px-1.5 py-0.5 rounded">C</span></td>
                            <td className="px-2 py-1.5 font-medium">Observações</td>
                            <td className="px-2 py-1.5 text-gray-500">Texto livre</td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">Contagem OK</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-mono px-1.5 py-0.5 rounded">D</span></td>
                            <td className="px-2 py-1.5 font-medium">Hora Início</td>
                            <td className="px-2 py-1.5 text-gray-500"><code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">HH:MM</code></td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">08:30</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-mono px-1.5 py-0.5 rounded">E</span></td>
                            <td className="px-2 py-1.5 font-medium">Hora Término</td>
                            <td className="px-2 py-1.5 text-gray-500"><code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">HH:MM</code></td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">17:45</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-mono px-1.5 py-0.5 rounded">F</span></td>
                            <td className="px-2 py-1.5 font-medium">Envio Relatório</td>
                            <td className="px-2 py-1.5 text-gray-500"><code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">DD/MM/AAAA HH:MM</code></td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">04/12/2025 18:30</td>
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5"><span className="bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-mono px-1.5 py-0.5 rounded">G</span></td>
                            <td className="px-2 py-1.5 font-medium">Local(is) Contagem</td>
                            <td className="px-2 py-1.5 text-gray-500">Vírgula separa</td>
                            <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">Farmácia, Almox</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Dica sobre locais */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-3 rounded mb-4">
                    <p className="text-blue-800 dark:text-blue-200 text-xs flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span><strong>Coluna G (Locais):</strong> Separe múltiplos locais por vírgula. Locais novos são cadastrados automaticamente no sistema.</span>
                    </p>
                  </div>

                  {/* Comportamentos */}
                  <div className="mb-4">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 mb-2">⚙️ Comportamento da Importação</p>
                    <div className="bg-white dark:bg-gray-700 rounded p-3 text-xs border border-gray-200 dark:border-gray-600 space-y-1">
                      <p>• <strong>Campos preenchidos:</strong> Sobrescrevem o valor anterior</p>
                      <p>• <strong>Campos vazios:</strong> Mantêm o valor anterior (não apagam)</p>
                      <p>• <strong>Coluna G vazia:</strong> Mantém locais existentes vinculados</p>
                      <p>• <strong>Local novo:</strong> É criado automaticamente no cadastro</p>
                    </div>
                  </div>

                  {/* Erros comuns */}
                  <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
                    <p className="text-red-800 dark:text-red-200 text-xs font-medium mb-1">❌ Erros comuns:</p>
                    <ul className="text-red-700 dark:text-red-300 text-xs space-y-0.5 ml-4 list-disc">
                      <li><strong>DADOS_VAZIOS:</strong> Linha 2 está vazia - preencha pelo menos 1 campo</li>
                      <li><strong>FORMATO_HORA_INVALIDO:</strong> Use HH:MM (ex: 08:30, não 8:30)</li>
                      <li><strong>FORMATO_DATA_INVALIDO:</strong> Use DD/MM/AAAA HH:MM (ex: 04/12/2025 18:30)</li>
                      <li><strong>COLUNAS_INSUFICIENTES:</strong> O Excel precisa ter pelo menos 6 colunas</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-3">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">📋 Excel: 7 colunas obrigatórias</span>
                  </p>
                  <ul className="space-y-2">
                    <li><strong className="text-gray-900 dark:text-white">A:</strong> Código</li>
                    <li><strong className="text-gray-900 dark:text-white">B:</strong> Descrição</li>
                    <li><strong className="text-gray-900 dark:text-white">C:</strong> Lote</li>
                    <li><strong className="text-gray-900 dark:text-white">D:</strong> Validade (DD/MM/YYYY)</li>
                    <li><strong className="text-gray-900 dark:text-white">E:</strong> Qtde</li>
                    <li><strong className="text-gray-900 dark:text-white">F:</strong> GTIN/EAN* <span className="text-gray-500 dark:text-gray-400">(Opcional)</span></li>
                    <li><strong className="text-gray-900 dark:text-white">G:</strong> Cód. Cliente* <span className="text-gray-500 dark:text-gray-400">(Opcional)</span></li>
                  </ul>
                  <p className="mt-4 text-xs text-gray-600 dark:text-gray-400">
                    Total importados: 0 produto(s) • * Opcional
                  </p>
                  <p className="mt-4 text-xs text-yellow-600 dark:text-yellow-400 font-medium border-t border-blue-200 dark:border-blue-700 pt-3">
                    ⚠️ Atenção: Você pode editar os valores "Sistema" e "Contado" clicando nas células da tabela. As alterações ficam em memória e serão mantidas até você atualizar a página.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
